import { useState, useEffect } from "react";
import { Box, Grid, CircularProgress, Typography, Divider, Stack } from "@mui/material";
import Swal from "sweetalert2";
import 'sweetalert2/dist/sweetalert2.min.css';
import api from "core/interceptors/api";
import MDButton from "components/MDButton";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useNavigate, useLocation } from "react-router-dom";
import logo from "assets/images/logo.png";

const PaymentPlansPage = () => {
  const [paymentPlans, setPaymentPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  // ✅ Get email & vendor type from navigation or localStorage
  const email = location.state?.email || localStorage.getItem("renew_email") || "";
  const vendorType =
    location.state?.vendor_type ||
    localStorage.getItem("renew_vendor_type") ||
    "";

  // Redirect if missing
  useEffect(() => {
    if (!email || !vendorType) {
      Swal.fire("Error", "Missing subscription renewal info.", "error");
      navigate("/login");
    }
  }, [email, vendorType, navigate]);

  // Fetch plans
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await api.get("/payment_plans/get");
        const allPlans = response.data || [];
        console.log(vendorType)
        // Filter by vendor type
        const filteredPlans = allPlans.filter(plan => {
          if (vendorType === "vendor") {
            return plan.plan_type === "vendor";
          } else {
            return plan.plan_type === "service";
          }
        });
        setPaymentPlans(filteredPlans);
        setLoading(false);
      } catch (error) {
        console.error("Failed to fetch payment plans:", error);
        Swal.fire("Error", "Unable to load payment plans.", "error");
        setLoading(false);
      }
    };

    if (vendorType) {
      fetchPlans();
    }
  }, [vendorType]);

  const handleRenew = async (plan) => {
    try {
      const payableAmount = plan.amount;

      // Step 1: Create Razorpay order
      const paymentInit = await api.post("/razor_payments/", {
        amount_paid: payableAmount,
        payment_method: "Online",
        user_id: email,
        type_of_vendor: vendorType, // ✅ send correct vendor type
        plan_name: plan.name,
        plan_validity_days: plan.validity_days
      });

      const {
        razorpay_order_id,
        razorpay_key_id,
        amount,
        payment_id
      } = paymentInit.data;

      // Step 2: Razorpay config
      const options = {
        key: razorpay_key_id,
        amount,
        currency: "INR",
        order_id: razorpay_order_id,
        name: `${plan.name} Plan`,
        description: "Subscription Renewal",
        handler: async function (response) {
          try {
            // Update payment record
            await api.put(`/razor_payments/${payment_id}`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              payment_status: "success",
              paid_at: new Date().toISOString(),
              user_id: email,
              type_of_vendor: vendorType // ✅ keep correct type
            });

            // Update subscription
            if (vendorType.toLowerCase() === "vendor") {
              try {
                await api.put("/vendor_register/renew_subscription", {
                  email,
                  no_of_images: plan.total_images_allowed,
                  no_of_notifications: plan.no_of_notifications
                });
              } catch (err) {
                console.error("Subscription renewal failed:", err);
                Swal.fire(
                  "Error",
                  "Subscription renewal failed. Please contact support.",
                  "error"
                );
                return;
              }
            }

            Swal.fire(
              "Success",
              "Your subscription has been renewed!",
              "success"
            ).then(() => navigate("/dashboard"));
          } catch (err) {
            console.error("Renewal API error", err);
            Swal.fire(
              "Error",
              "Payment succeeded but failed to renew subscription.",
              "error"
            );
          }
        },
        prefill: { email },
        theme: { color: "#F37254" }
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", () => {
        Swal.fire("Payment Failed", "Please try again.", "error");
      });
      rzp.open();
    } catch (err) {
      console.error("Payment initiation failed", err);
      Swal.fire("Error", "Could not start payment. Please try again.", "error");
    }
  };

  if (loading) {
    return (
      <Box textAlign="center" py={5}>
        <CircularProgress />
        <Typography variant="h6" mt={2}>
          Loading Plans...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        minHeight: "100vh",
        bgcolor: "#f9fafc",
        py: 5,
      }}
    >
      <Box sx={{ mb: 2 }}>
        <img
          src={logo}
          alt="Company Logo"
          style={{ width: "100px", height: "auto", objectFit: "contain" }}
        />
      </Box>
      <Typography
        variant="h4"
        fontWeight="bold"
        gutterBottom
        sx={{ color: "#1976d2", mb: 4 }}
      >
        Renew Your Subscription
      </Typography>

      <Grid
        container
        spacing={4}
        justifyContent="center"
        sx={{ maxWidth: "1200px" }}
      >
        {paymentPlans.map((plan) => (
          <Grid item xs={12} sm={6} md={4} key={plan.id}>
            <Card
              sx={{
                position: "relative",
                borderRadius: "20px",
                border:
                  selectedPlan?.id === plan.id
                    ? "2px solid #1976d2"
                    : "1px solid #ddd",
                boxShadow:
                  selectedPlan?.id === plan.id
                    ? "0px 8px 24px rgba(25, 118, 210, 0.4)"
                    : "0px 6px 20px rgba(0, 0, 0, 0.08)",
                transition: "transform 0.3s ease, box-shadow 0.3s ease",
                "&:hover": {
                  transform: "translateY(-6px)",
                  boxShadow: "0px 12px 32px rgba(0, 0, 0, 0.15)",
                },
                cursor: "pointer",
              }}
              onClick={() => setSelectedPlan(plan)}
            >
              {selectedPlan?.id === plan.id && (
                <CheckCircleIcon
                  sx={{
                    position: "absolute",
                    top: 10,
                    right: 10,
                    color: "#1976d2",
                    fontSize: "28px",
                  }}
                />
              )}
              <CardContent sx={{ textAlign: "center", p: 4 }}>

                <Typography variant="h5" fontWeight="bold" gutterBottom>
                  {plan.name}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ minHeight: "48px" }}
                >
                  {plan.description}
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography
                  variant="h3"
                  fontWeight="bold"
                  sx={{ color: "#1976d2" }}
                >
                  ₹{plan.amount}
                </Typography>
                <Stack mt={2} spacing={1}>
                  <Typography variant="body2">
                    📷 {plan.total_images_allowed} Image Uploads
                  </Typography>
                  <Typography variant="body2">
                    🔔 {plan.no_of_notifications} Notifications
                  </Typography>
                  <Typography variant="body2">⏳ {plan.period} Days Plan</Typography>
                </Stack>
                <MDButton
                  fullWidth
                  variant="contained"
                  color="info"
                  sx={{
                    mt: 3,
                    borderRadius: "30px",
                    py: 1.2,
                    fontWeight: "bold",
                    textTransform: "uppercase",
                    fontSize: "0.9rem",
                  }}
                  onClick={() => handleRenew(plan)}
                >
                  Renew Now
                </MDButton>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );

};

export default PaymentPlansPage;
